import { createContext, useContext, useReducer, useEffect, useCallback, useRef, type ReactNode } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

// ─── TYPES ─────────────────────────────────────────────

export interface Profile {
  id: string;
  full_name: string;
  phone: string;
  role: string;
  is_approved: boolean;
  username: string;
  shift_type: string;
  base_salary: number;
  commission_per_signal: number;
  is_admin: boolean;
  is_verified: boolean;
  created_at: string;
  updated_at: string;
  job_category: string | null;
}

// ─── ADMIN BYPASS MOCK DATA ───────────────────────────

const ADMIN_PASSWORD = 'SirMax';

const MOCK_ADMIN_USER: User = {
  id: 'admin-bypass-0000-0000-000000000000',
  email: 'admin@carland.internal',
  role: 'authenticated',
  aud: 'authenticated',
  app_metadata: {},
  user_metadata: {},
  created_at: new Date().toISOString(),
} as User;

const MOCK_ADMIN_PROFILE: Profile = {
  id: 'admin-bypass-0000-0000-000000000000',
  full_name: 'مدیر سیستم',
  phone: '',
  role: 'admin',
  is_approved: true,
  username: 'SirMax',
  shift_type: 'admin',
  base_salary: 0,
  commission_per_signal: 0,
  is_admin: true,
  is_verified: true,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
};

// ─── AUTH STATE ────────────────────────────────────────

interface AuthState {
  user: User | null;
  profile: Profile | null;
  session: Session | null;
  loading: boolean;
  adminBypass: boolean;
}

type AuthAction =
  | { type: 'INIT_DONE' }
  | { type: 'SET_AUTH'; user: User; session: Session; profile: Profile }
  | { type: 'CLEAR_AUTH' }
  | { type: 'SET_LOADING' }
  | { type: 'ADMIN_BYPASS' }
  | { type: 'CLEAR_ADMIN_BYPASS' };

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'INIT_DONE':
      return { ...state, loading: false };
    case 'SET_AUTH':
      return {
        user: action.user,
        session: action.session,
        profile: action.profile,
        loading: false,
        adminBypass: false, // Real auth overrides bypass
      };
    case 'CLEAR_AUTH':
      if (state.adminBypass) {
        return { ...state, loading: false };
      }
      return {
        user: null,
        session: null,
        profile: null,
        loading: false,
        adminBypass: false,
      };
    case 'SET_LOADING':
      return { ...state, loading: true };
    case 'ADMIN_BYPASS':
      return {
        user: MOCK_ADMIN_USER,
        session: null,
        profile: MOCK_ADMIN_PROFILE,
        loading: false,
        adminBypass: true,
      };
    case 'CLEAR_ADMIN_BYPASS':
      return { ...state, adminBypass: false };
    default:
      return state;
  }
}

// ─── CONTEXT ───────────────────────────────────────────

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  signIn: (username: string, password: string) => Promise<{ error: string | null; isAdmin?: boolean }>;
  loginAsAdmin: (password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  createUserByAdmin: (data: {
    username: string;
    password: string;
    full_name: string;
    shift_type: string;
    commission_per_signal?: number;
    base_salary?: number;
  }) => Promise<{ error: string | null; user?: Record<string, unknown> }>;
  updateUserByAdmin: (userId: string, data: Record<string, unknown>) => Promise<{ error: string | null }>;
  deleteUserByAdmin: (userId: string) => Promise<{ error: string | null }>;
  getAllUsers: () => Promise<{ data: Profile[] | null; error: string | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const ADMIN_EMAIL_DOMAIN = '@carland.internal';

// ─── PROVIDER ──────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, {
    user: null,
    profile: null,
    session: null,
    loading: true,
    adminBypass: false,
  });

  const { user, profile, session, loading, adminBypass } = state;
  const isAdmin = profile?.is_admin ?? false;

  // Flag to prevent onAuthStateChange from interfering with explicit login flows
  const loginInProgressRef = useRef(false);

  // ── HELPERS ──────────────────────────────────────────

  const fetchProfile = useCallback(async (userId: string): Promise<Profile | null> => {
    // First confirm the session is synced — prevents RLS race condition
    const { data: { session: s } } = await supabase.auth.getSession();
    const effectiveId = s?.user?.id || userId;

    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', effectiveId)
      .maybeSingle();
    return data as Profile | null;
  }, []);

  const refreshProfile = useCallback(async () => {
    if (!user || adminBypass) return;
    const p = await fetchProfile(user.id);
    if (p && user && session) {
      dispatch({ type: 'SET_AUTH', user, session, profile: p });
    }
  }, [user, session, adminBypass, fetchProfile]);

  // ── INIT ─────────────────────────────────────────────

  useEffect(() => {
    let mounted = true;

    const initAuth = async () => {
      try {
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        if (!mounted) return;

        if (currentSession?.user) {
          const p = await fetchProfile(currentSession.user.id);
          if (mounted && p) {
            dispatch({ type: 'SET_AUTH', user: currentSession.user, session: currentSession, profile: p });
            return;
          }
          if (mounted) {
            await supabase.auth.signOut();
            dispatch({ type: 'CLEAR_AUTH' });
            return;
          }
        }
        if (mounted) dispatch({ type: 'INIT_DONE' });
      } catch {
        if (mounted) dispatch({ type: 'INIT_DONE' });
      }
    };

    initAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, currentSession) => {
      if (!mounted) return;

      // If an explicit login is in progress, let it handle state
      if (loginInProgressRef.current) return;

      if (event === 'SIGNED_OUT') {
        dispatch({ type: 'CLEAR_AUTH' });
        return;
      }

      if (event === 'SIGNED_IN' && currentSession?.user) {
        const p = await fetchProfile(currentSession.user.id);
        if (mounted && p) {
          dispatch({ type: 'SET_AUTH', user: currentSession.user, session: currentSession, profile: p });
        }
        // If fetchProfile fails, DON'T sign out — explicit login flow handles this
        return;
      }

      // TOKEN_REFRESHED or USER_UPDATED
      if (currentSession?.user) {
        const p = await fetchProfile(currentSession.user.id);
        if (mounted && p) {
          dispatch({ type: 'SET_AUTH', user: currentSession.user, session: currentSession, profile: p });
        }
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [fetchProfile]);

  // ── USER SIGN IN (via Supabase) ──────────────────────

  const signIn = useCallback(async (username: string, password: string) => {
    const normalizedUsername = username.trim();
    const email = `${normalizedUsername}${ADMIN_EMAIL_DOMAIN}`;

    loginInProgressRef.current = true;
    dispatch({ type: 'SET_LOADING' });

    const { data, error } = await supabase.auth.signInWithPassword({ email, password });

    if (error || !data.user) {
      loginInProgressRef.current = false;
      dispatch({ type: 'CLEAR_AUTH' });
      if (error?.message?.includes('Invalid login credentials')) {
        return { error: 'نام کاربری یا رمز عبور اشتباه است' };
      }
      return { error: 'خطا در ورود. لطفاً دوباره تلاش کنید' };
    }

    // Fetch profile via edge function (service role — bypasses RLS completely)
    let p: Profile | null = null;
    try {
      const profileResp = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/admin-users/get-profile`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-admin-bypass': ADMIN_PASSWORD,
          },
          body: JSON.stringify({ user_id: data.user.id }),
        }
      );
      const profileResult = await profileResp.json();
      if (profileResult.profile) {
        p = profileResult.profile as Profile;
      }
    } catch {
      // Fallback to direct RLS query
    }

    // Fallback: try direct RLS query if edge function failed
    if (!p) {
      p = await fetchProfile(data.user.id);
    }

    if (!p) {
      loginInProgressRef.current = false;
      await supabase.auth.signOut();
      dispatch({ type: 'CLEAR_AUTH' });
      return { error: 'حساب کاربری یافت نشد. لطفاً با پشتیبانی تماس بگیرید' };
    }

    loginInProgressRef.current = false;
    dispatch({ type: 'SET_AUTH', user: data.user, session: data.session, profile: p });

    return { error: null, isAdmin: p.is_admin ?? false };
  }, [fetchProfile]);

  // ── ADMIN LOGIN (local password check → ensure admin → Supabase login) ─

  const loginAsAdmin = useCallback(async (password: string): Promise<{ error: string | null }> => {
    if (password !== ADMIN_PASSWORD) {
      return { error: 'رمز عبور مدیر اشتباه است' };
    }

    loginInProgressRef.current = true;
    dispatch({ type: 'SET_LOADING' });

    // Step 1: Ensure admin auth user exists with correct password
    try {
      await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/admin-users/ensure-admin`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username: 'SirMax',
            password: ADMIN_PASSWORD,
            full_name: 'مدیر سیستم',
          }),
        }
      );
    } catch {
      // ensure-admin may fail — proceed to login anyway
    }

    // Step 2: Try Supabase login
    try {
      const email = `SirMax${ADMIN_EMAIL_DOMAIN}`;
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password: ADMIN_PASSWORD,
      });

      if (!error && data?.user) {
        const p = await fetchProfile(data.user.id);
        if (p) {
          loginInProgressRef.current = false;
          dispatch({
            type: 'SET_AUTH',
            user: data.user,
            session: data.session,
            profile: p,
          });
          return { error: null };
        }
      }
    } catch {
      // Supabase login failed
    }

    // Step 3: Fallback — bypass with mock data
    loginInProgressRef.current = false;
    dispatch({ type: 'ADMIN_BYPASS' });
    return { error: null };
  }, [fetchProfile]);

  // ── SIGN OUT ─────────────────────────────────────────

  const signOut = useCallback(async () => {
    dispatch({ type: 'CLEAR_ADMIN_BYPASS' });
    try {
      await supabase.auth.signOut();
    } catch {
      // ignore
    }
    dispatch({ type: 'CLEAR_AUTH' });
  }, []);

  // ── ADMIN: CREATE USER ───────────────────────────────

  const createUserByAdmin = useCallback(async (data: {
    username: string;
    password: string;
    full_name: string;
    shift_type: string;
    commission_per_signal?: number;
    base_salary?: number;
  }) => {
    try {
      // Try to get the session token
      let token = '';
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      token = currentSession?.access_token ?? '';

      // If no session token from Supabase, always try bypass header
      if (!token) {
        const resp = await fetch(
          `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/admin-users/create-user`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'x-admin-bypass': ADMIN_PASSWORD,
            },
            body: JSON.stringify(data),
          }
        );
        const result = await resp.json();
        if (result.error) return { error: result.error as string };
        return { error: null, user: result.user };
      }

      if (!token) {
        return { error: 'ابتدا لاگین کنید' };
      }

      const resp = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/admin-users/create-user`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(data),
        }
      );
      const result = await resp.json();
      if (result.error) return { error: result.error as string };
      return { error: null, user: result.user };
    } catch {
      return { error: 'خطا در اتصال به سرور' };
    }
  }, []);

  // ── ADMIN: UPDATE USER ───────────────────────────────

  const updateUserByAdmin = useCallback(async (userId: string, data: Record<string, unknown>) => {
    try {
      let token = '';
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      token = currentSession?.access_token ?? '';

      if (!token) {
        const resp = await fetch(
          `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/admin-users/update-user`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'x-admin-bypass': ADMIN_PASSWORD,
            },
            body: JSON.stringify({ user_id: userId, ...data }),
          }
        );
        const result = await resp.json();
        if (result.error) return { error: result.error as string };
        return { error: null };
      }

      if (!token) {
        return { error: 'ابتدا لاگین کنید' };
      }

      const resp = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/admin-users/update-user`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ user_id: userId, ...data }),
        }
      );
      const result = await resp.json();
      if (result.error) return { error: result.error as string };
      return { error: null };
    } catch {
      return { error: 'خطا در اتصال به سرور' };
    }
  }, []);

  // ── ADMIN: DELETE USER ───────────────────────────────

  const deleteUserByAdmin = useCallback(async (userId: string) => {
    try {
      let token = '';
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      token = currentSession?.access_token ?? '';

      if (!token) {
        const resp = await fetch(
          `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/admin-users/delete-user`,
          {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              'x-admin-bypass': ADMIN_PASSWORD,
            },
            body: JSON.stringify({ user_id: userId }),
          }
        );
        const result = await resp.json();
        if (result.error) return { error: result.error as string };
        return { error: null };
      }

      if (!token) {
        return { error: 'ابتدا لاگین کنید' };
      }

      const resp = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/admin-users/delete-user`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ user_id: userId }),
        }
      );
      const result = await resp.json();
      if (result.error) return { error: result.error as string };
      return { error: null };
    } catch {
      return { error: 'خطا در اتصال به سرور' };
    }
  }, []);

  const getAllUsers = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('is_admin', false)
        .order('created_at', { ascending: false });

      if (error) return { data: null, error: error.message };
      return { data: data as Profile[], error: null };
    } catch {
      return { data: null, error: 'خطا در دریافت لیست کاربران' };
    }
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      profile,
      session,
      loading,
      isAdmin,
      signIn,
      loginAsAdmin,
      signOut,
      refreshProfile,
      createUserByAdmin,
      updateUserByAdmin,
      deleteUserByAdmin,
      getAllUsers,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

// ─── HOOK ──────────────────────────────────────────────

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}